/**
 * Scene Library runtime (spec section 31).
 *
 * Merges the shipped scene definitions with everything the user has
 * imported or generated, tracks usage for the selection engine, and
 * owns the "does this scene have a usable asset?" question.
 */

import { SCENES, cloneScene, LOOP_QUALITIES } from '../data/scenes.js';
import { uid } from '../utils/misc.js';

export function createSceneStore(initial = SCENES) {
  let items = initial.map(cloneScene);

  /**
   * Every read returns a clone. A view that accidentally mutates the
   * object it was handed must not be able to corrupt the library.
   */
  const api = {
    all() {
      return items.map(cloneScene);
    },
    enabled() {
      return items.filter((s) => s.enabled !== false).map(cloneScene);
    },
    get(id) {
      const found = items.find((s) => s.id === id);
      return found ? cloneScene(found) : null;
    },
    byMood(mood) {
      if (!mood) return api.all();
      return items.filter((s) => s.moods.includes(mood)).map(cloneScene);
    },
    byTag(tag) {
      return items.filter((s) => s.tags.includes(tag)).map(cloneScene);
    },
    /** Scenes that can be rendered right now, without generating anything. */
    renderable() {
      return items
        .filter((s) => (s.assetType === 'procedural' && s.proceduralId) || (s.assetType === 'video' && s.assetUrl))
        .map(cloneScene);
    },
    /** Scenes still waiting for an asset - these are the "Generate Scene" targets. */
    pending() {
      return items
        .filter((s) => s.assetType === 'none' || (s.assetType === 'video' && !s.assetUrl))
        .map(cloneScene);
    },
    add(scene) {
      const next = { ...cloneScene(scene), id: scene.id ?? uid('scene'), usageCount: 0, lastUsed: null };
      items = [next, ...items];
      return cloneScene(next);
    },
    update(id, patch) {
      const index = items.findIndex((s) => s.id === id);
      if (index === -1) return null;
      const next = { ...items[index], ...patch };
      items = items.map((s, i) => (i === index ? next : s));
      return cloneScene(next);
    },
    remove(id) {
      const before = items.length;
      items = items.filter((s) => s.id !== id);
      return items.length !== before;
    },
    duplicate(id) {
      const source = api.get(id);
      if (!source) return null;
      const copy = cloneScene(source);
      copy.id = `${source.id}-copy-${Math.random().toString(36).slice(2, 6)}`;
      copy.name = `${source.name} Copy`;
      copy.usageCount = 0;
      copy.lastUsed = null;
      items = [copy, ...items];
      return cloneScene(copy);
    },
    setEnabled(id, enabled) {
      return api.update(id, { enabled: Boolean(enabled) });
    },
    /** Attach a validated asset to a scene and mark it as a video scene. */
    attachAsset(id, asset) {
      return api.update(id, {
        assetType: 'video',
        assetUrl: asset.blobUrl ?? null,
        assetId: asset.assetId ?? null,
        duration: asset.durationSeconds ?? api.get(id)?.duration ?? 10,
        loopQuality: asset.loopQuality ?? 'unknown',
        provider: asset.providerId ?? 'local-library',
        assetMeta: asset.meta ?? null,
      });
    },
    setLoopQuality(id, loopQuality) {
      if (!LOOP_QUALITIES.includes(loopQuality)) {
        throw new Error(`loopQuality must be one of ${LOOP_QUALITIES.join(', ')}`);
      }
      return api.update(id, { loopQuality });
    },
    /** Called after an episode render so the selection engine can de-prioritise it. */
    recordUsage(id, when = new Date().toISOString()) {
      const scene = api.get(id);
      if (!scene) return null;
      return api.update(id, { usageCount: (scene.usageCount ?? 0) + 1, lastUsed: when });
    },
    replaceAll(list) {
      items = list.map(cloneScene);
    },
    /** Merge persisted overrides onto the shipped defaults. */
    mergeOverrides(overrides) {
      const byId = new Map(items.map((s) => [s.id, s]));
      for (const override of overrides) {
        const existing = byId.get(override.id);
        byId.set(override.id, existing ? { ...existing, ...override } : cloneScene(override));
      }
      items = Array.from(byId.values());
    },
    size() {
      return items.length;
    },
  };

  return api;
}

/** Integrity check used by tests and by the Settings > Diagnostics panel. */
export function validateScenes(scenes, requiredFields = null) {
  const issues = [];
  const seen = new Set();
  for (const scene of scenes) {
    if (seen.has(scene.id)) issues.push(`duplicate scene id: ${scene.id}`);
    seen.add(scene.id);
    if (requiredFields) {
      for (const field of requiredFields) {
        if (scene[field] === undefined) issues.push(`${scene.id}: missing ${field}`);
      }
    }
    if (!Array.isArray(scene.moods) || scene.moods.length === 0) issues.push(`${scene.id}: no moods`);
    if (!Array.isArray(scene.tags) || scene.tags.length === 0) issues.push(`${scene.id}: no tags`);
    if (!(scene.duration > 0)) issues.push(`${scene.id}: invalid duration`);
    if (scene.assetType === 'procedural' && !scene.proceduralId) {
      issues.push(`${scene.id}: assetType is procedural but proceduralId is missing`);
    }
    if (!LOOP_QUALITIES.includes(scene.loopQuality)) issues.push(`${scene.id}: invalid loopQuality`);
  }
  return issues;
}
